import { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
    CenterModal,
    ModalSize,
    Button,
    ButtonVariant,
} from "@repo/design-system/components/ui";
import { FormBuilder } from "@repo/design-system/components/forms";
import { ToastService } from "@gyandiksha/shared-ui/services";
import { useLazyCheckPasswordQuery } from "@gyandiksha/shared-ui/app/services";
import { passwordFormConfig } from "./config/passwordVerification.config.jsx";

export const PasswordVerificationModal = ({ isOpen, onClose, onSuccess }) => {

    const formRef = useRef(null);
    const [formData, setFormData] = useState({ password: "" });
    const [checkPassword, { isLoading, isFetching }] = useLazyCheckPasswordQuery();

    useEffect(() => {
        if (isOpen) {
            setFormData({ password: "" });
        }
    }, [isOpen]);

    const handleVerifyPassword = useCallback(async (e) => {
        const formData = formRef.current?.getValidatedFormData();
        if (formData) {
            const res = await checkPassword(formData.password).unwrap();
            const isVerified = res?.data?.isVerified ?? res?.isVerified;

            if (isVerified) {
                setFormData({ password: "" });
                ToastService.success({
                    title: "Identity Verified",
                    description: "Sensitive information has been unlocked and is now visible.",
                });
                onSuccess();
            } else {
                ToastService.error({
                    title: "Incorrect Password",
                    description: "The password you entered is incorrect. Please try again.",
                });
            }
        }
    }, [checkPassword, formData.password, onSuccess]);

    return (
        <CenterModal isOpen={isOpen} handleToggleModal={onClose}
            modalTitle="Password Verification" modelSizeClass={ModalSize.WD25}
            footerChildren={
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button variant={ButtonVariant.OUTLINE}
                        leftIcon="material-symbols:close-rounded"
                        onClick={onClose} disabled={isLoading}>
                        Close
                    </Button>
                    <Button loading={isLoading || isFetching} onClick={handleVerifyPassword}
                        leftIcon="material-symbols:lock-open-rounded">
                        Verify Password
                    </Button>
                </div>
            }>
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyPassword(e); }}>
                <FormBuilder ref={formRef} config={passwordFormConfig} />
            </form>
        </CenterModal>
    );
};

PasswordVerificationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
};
